// Hiring's own pages, for everyone. `group` names the folding block a page
// sits in (frame 1.6.0, C16, 29 Oct 2026): the pages without one come first,
// under Hiring; the ERP's frame reads the same field from setPages.
export const HIRING_PAGES = [
  { value: '/dashboard', label: 'Dashboard', icon: 'squares-four' },
  { value: '/jobs', label: 'Jobs', icon: 'briefcase' },
  // A panellist's own interviews to mark (HIR-TC1).
  { value: '/my-interviews', label: 'My interviews', icon: 'clipboard-text' },
  // Below a pass mark for the first time: waiting for a named conductor
  // (HIR-TC1). Not offered to the ERP's list, which is already at its cap;
  // there it is reached from a stage's "waiting in the exit filter" note.
  { value: '/exit-filter', label: 'Exit filter', icon: 'funnel', erp: false },
  { value: '/talent-pool', label: 'Talent pool', icon: 'users' },
  // The Head's views, also useful to HR, who put candidates forward (HIR-HD1, HIR-HD2).
  { value: '/pipeline', label: 'Pipeline', icon: 'chart-bar' },
  { value: '/shortlists', label: 'Shortlists', icon: 'list-checks' },
  // Assessments waiting for the school's academic approval (HIR-HR2).
  { value: '/assessment-approvals', label: 'Assessment approvals', icon: 'check-circle' },
  // Messages to applicants as campaigns: compose, send, report (HIR-HR11..16).
  { value: '/campaigns', label: 'Campaigns', icon: 'paper-plane-right', group: 'Messages' },
  // What message went to which applicant, by email or WhatsApp (HIR-HR16).
  { value: '/messages-sent', label: 'Messages sent', icon: 'paper-plane', group: 'Messages' },
  { value: '/health', label: 'Health', icon: 'pulse', group: 'Setup' },
  { value: '/settings', label: 'Settings', icon: 'gear', group: 'Setup' },
];

/* What the ERP lists under Hiring in its own sidebar (window.equall.setPages,
   DPS-ERP-Console docs/MICROFRONTENDS.md §4a): one flat list of at most 12,
   so the admin pages say they are Admin's, and Health goes last, being the
   one to miss if the list is cut. */
export function erpPages(isAdmin, platform = false) {
  const own = HIRING_PAGES.filter((p) => p.value !== '/health' && p.erp !== false);
  const admin = isAdmin ? adminPagesFor(platform).map((p) => ({ ...p, label: `Admin · ${p.label}`, group: 'Admin' })) : [];
  const health = HIRING_PAGES.filter((p) => p.value === '/health');
  return [...own, ...admin, ...health];
}

// Hiring's admin pages, one address each, listed in the sidebar under
// "Admin" (one nav pattern, Sanjoy 3 Oct 2026: pages live in the left
// sidebar, never in tabs across the top).
//
// Three pages only (owner, 29 Oct 2026): Users, Interview prompts and the
// Cost ledger, which now shows what the AI providers charged (the separate
// "AI provider cost" page merged into it). Overview's figures moved to the
// Dashboard; Token management, System config and the Assessment library
// went. Their old addresses still land (RETIRED_ADMIN_PAGES).
export const ADMIN_PAGES = [
  { key: 'users',   value: '/admin/users',   label: 'Users',             icon: 'users-three' },
  /* The interviewer's base prompts are EquaOS's, not a school's (owner,
     30 Oct 2026): schools set up each interview with its brief, on the
     role's AI interview stage. Listed on the platform tier only. */
  { key: 'prompts', value: '/admin/prompts', label: 'EquaOS base prompts', icon: 'chat-circle-text', equaosOnly: true },
  { key: 'ledger',  value: '/admin/ledger',  label: 'Cost ledger',       icon: 'money' },
];

/* The admin pages a person sees: the EquaOS-only ones on the platform tier
   (config/base PLATFORM_TIER) only. */
export function adminPagesFor(platform) {
  return ADMIN_PAGES.filter((p) => platform || !p.equaosOnly);
}

/* Where each retired admin address now goes, so a bookmark or a link already
   sent still lands somewhere sensible. */
export const RETIRED_ADMIN_PAGES = {
  '/admin/dashboard': '/dashboard',
  '/admin/ai-cost': '/admin/ledger',
  '/admin/tokens': '/admin/users',
  '/admin/config': '/admin/users',
  '/admin/assessments': '/admin/users',
};

/* The admin page an address names: /admin/<section>, or Users. */
export function adminPageFor(pathname) {
  return ADMIN_PAGES.find((p) => pathname === p.value) || ADMIN_PAGES[0];
}

/* The sidebar's blocks, in order: Hiring's pages with no block first, then
   each block as the pages name it, then Admin for administrators. */
export function navBlocks(hiring, admin) {
  const blocks = [{ label: 'Hiring', pages: hiring.filter((p) => !p.group) }];
  for (const p of hiring.filter((x) => x.group)) {
    let b = blocks.find((x) => x.label === p.group);
    if (!b) { b = { label: p.group, pages: [] }; blocks.push(b); }
    b.pages.push(p);
  }
  if (admin.length) blocks.push({ label: 'Admin', pages: admin });
  return blocks.filter((b) => b.pages.length);
}
